import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileApi, authApi } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useToast } from '@/hooks/ui/use-toast'

/**
 * Mutation for updating the authenticated user's profile (text fields only)
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { login } = useAuth()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await profileApi.update(data)
      return response.data
    },
    onSuccess: async () => {
      const refreshed = await authApi.me()
      login(refreshed.data.user)
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast({
        title: 'Success!',
        description: 'Profile updated',
      })
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.non_field_errors?.[0] ||
        'Unable to update your profile. Please try again.'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })
}

/**
 * Mutation for uploading user avatar
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (file: File) => {
      const response = await profileApi.uploadAvatar(file)
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast({
        title: 'Success!',
        description: 'Avatar uploaded',
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Unable to upload avatar. Please try again.'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })
}

/**
 * Mutation for removing user avatar
 */
export function useRemoveAvatar() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async () => {
      const response = await profileApi.removeAvatar()
      return response.data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast({
        title: 'Success!',
        description: 'Avatar removed',
      })
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Unable to remove avatar. Please try again.'
      toast({
        variant: 'destructive',
        title: 'Error',
        description: message,
      })
    },
  })
}
